import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'

import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

import {
  useDocumentDataSource,
  useNamedQuery,
  type NamedResult,
  type WidgetNodeName,
} from './data-source'
import {
  changeValueText,
  evaluateCondition,
  refQueryName,
  repeatOverAt,
  valueText,
  type ConditionAttrs,
} from './resolve'

/*
 * Belge düğümlerinin React görünümleri. Hepsi veriyi `DocumentDataSource` bağlamından okur
 * (canlı ya da donmuş); kaynağı bilmezler. Düğüm öznitelikleri sözleşmedeki (şema) adlarıyla gelir.
 */

const t = messages.documents.nodes

/** Widget düğümü: `reportKpi` / `reportTable` / `reportList` / `reportChart`. */
export function WidgetNodeView({ node, selected }: NodeViewProps) {
  const { Widget } = useDocumentDataSource()
  return (
    <NodeViewWrapper
      data-testid={`node-${node.type.name}`}
      contentEditable={false}
      className={cn('my-3 rounded-md', selected && 'ring-primary ring-2')}
    >
      <Widget type={node.type.name as WidgetNodeName} attrs={node.attrs} />
    </NodeViewWrapper>
  )
}

function resultState(name: string | undefined, result: NamedResult | undefined) {
  return name === undefined ? 'ready' : (result?.state ?? 'ready')
}

/** `value` ve `change`: satır içi atom; metin kaynaktan çözülür. */
export function InlineRefView({ node, editor, getPos, selected }: NodeViewProps) {
  const source = useDocumentDataSource()
  const of = node.attrs.of as string
  const over = repeatOverAt(editor.state.doc, getPos())
  const name = refQueryName(of, over)
  const result = useNamedQuery(name)
  const state = resultState(name, result)

  let content: string
  if (state === 'loading') content = t.loading
  else if (state === 'error') content = t.error
  else if (state === 'forbidden') content = t.forbidden
  else if (node.type.name === 'change') content = changeValueText(of, result)
  else content = valueText(of, source, result)

  return (
    <NodeViewWrapper
      as="span"
      data-testid={`node-${node.type.name}`}
      title={of}
      className={cn(
        'bg-accent text-accent-foreground tabular rounded-sm px-1 font-medium',
        state !== 'ready' && 'text-muted-foreground font-normal',
        selected && 'ring-primary ring-2',
      )}
    >
      {content}
    </NodeViewWrapper>
  )
}

/** `condition`: iki dal da görünür; koşul canlı değerlendirilir, aktif dal vurgulanır. */
export function ConditionNodeView({ node, editor, getPos, selected }: NodeViewProps) {
  const attrs = node.attrs as ConditionAttrs
  const over = repeatOverAt(editor.state.doc, getPos())
  const leftName = refQueryName(attrs.left, over)
  const rightName = typeof attrs.right === 'string' ? refQueryName(attrs.right, over) : undefined
  const left = useNamedQuery(leftName)
  const right = useNamedQuery(rightName)
  const loading =
    resultState(leftName, left) === 'loading' || resultState(rightName, right) === 'loading'
  const active = loading ? undefined : evaluateCondition(attrs, left, right) ? 'then' : 'else'

  return (
    <NodeViewWrapper
      data-testid="node-condition"
      data-active={active}
      className={cn(
        'group/cond border-border my-3 rounded-md border border-dashed p-2',
        selected && 'ring-primary ring-2',
      )}
    >
      <p contentEditable={false} className="text-muted-foreground mb-1 text-xs select-none">
        {t.condition.header(attrs.left, t.condition.ops[attrs.op], String(attrs.right))}{' '}
        <span data-testid="condition-active" className="text-foreground font-medium">
          {active === undefined
            ? t.loading
            : active === 'then'
              ? t.condition.activeThen
              : t.condition.activeElse}
        </span>
      </p>
      <NodeViewContent />
    </NodeViewWrapper>
  )
}

/** `repeat`: şablonda bir kez (örnek ilk satırla) gösterilir; kopyada her satıra açılır. */
export function RepeatNodeView({ node, selected }: NodeViewProps) {
  const over = node.attrs.over as string
  const result = useNamedQuery(over)
  const count = result?.state === 'ready' ? (result.rows?.length ?? 0) : undefined
  return (
    <NodeViewWrapper
      data-testid="node-repeat"
      className={cn(
        'border-border my-3 rounded-md border border-dashed p-2',
        selected && 'ring-primary ring-2',
      )}
    >
      <p contentEditable={false} className="text-muted-foreground mb-1 text-xs select-none">
        {t.repeat.header(over, node.attrs.limit as number)}
        {count !== undefined && ` ${t.repeat.rows(count)}`}
      </p>
      <NodeViewContent />
    </NodeViewWrapper>
  )
}
